// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import { connect } from 'react-redux';
import Router from 'next/router';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import Image from '@tcp/core/src/components/common/atoms/Image';
import { callNavigateAPIForExternalLinks, processPath } from '@tcp/core/src/utils/utils';
import isEmpty from 'lodash/isEmpty';
import { getViewportInfo, getBrand } from '@tcp/core/src/utils';
import { KEY_CODES } from '@tcp/core/src/constants/keyboard.constants';
import { removeL1PanelTimeOut } from '@tcp/core/src/components/common/organisms/Header/container/Header.actions';
import { crossBrandRedirect } from '@tcp/core/src/utils/utils.web';
import pushEventToAdobeAnalytics from '@tcp/core/src/adobeAnalytics/adobe.utils';
import PromoBadge from '../PromoBadge';
import style from './L1NavItem.style';
import { DELAY_TO_OPEN, DELAY_TO_CLOSE } from './L1NavItem.config';
import { brand } from '../../../../../../constants';

const HideDrawerContext = React.createContext({});
const HideDrawerProvider = HideDrawerContext.Provider;
export const HideDrawerConsumer = HideDrawerContext.Consumer;

/**
 * This function highlights clearance links in red color on the base of id in unbxd
 * @param {*} id
 */
const highlightContent = (id) => {
  return id === '505518' || id === '454010' ? `highlighted` : ``;
};

class L1NavItem extends React.PureComponent {
  constructor() {
    super();
    this.state = {
      hovered: false,
      isOpen: false,
      showNavigationImages: false,
      hasMounted: false,
    };
    this.timeOutHandler = null;
    this.debouncedOnHover = debounce(this.onHover, 300);
    this.debouncedOnMouseLeave = debounce(this.onMouseLeave, 300);
  }

  componentDidMount() {
    const { showCondensedHeader, isNewNavigationImagesEnabled } = this.props;
    if (!showCondensedHeader && isNewNavigationImagesEnabled) {
      this.setState({
        showNavigationImages: true,
        hasMounted: true,
      });
    }
  }

  componentWillUnmount() {
    document.body.removeEventListener('keydown', this.handleDocumentKeydown);
    Router.events.off('routeChangeComplete', this.handleFocusBlur); // Ensure Router events are removed
    clearTimeout(this.timeOutHandler); // Clear any existing timeouts
  }

  /**
   * This function handles document keydown event when L2 drawer is opened
   */
  handleDocumentKeydown = (e) => {
    const { dataLocator } = this.props;
    if (
      e.keyCode === KEY_CODES.KEY_TAB ||
      (e.keyCode === KEY_CODES.KEY_TAB && e.shiftKey) ||
      e.keyCode === KEY_CODES.KEY_ENTER ||
      (e.keyCode === KEY_CODES.KEY_SPACE &&
        !document.getElementById(dataLocator).contains(document.activeElement))
    ) {
      // close drawer when focused element is not part of current opened drawer
      this.onMouseLeave();
    }
  };

  /**
   * This function will be used to open the l2 link items
   */
  onHover = (e) => {
    const { isOpen } = this.state;
    e.preventDefault();
    e.stopPropagation();
    if (getViewportInfo().isDesktop) {
      clearTimeout(this.timeOutHandler);
      if (isOpen) {
        this.setHoveredState(true);
      }
      if (!isOpen) {
        this.timeOutHandler = setTimeout(() => {
          this.setHoveredState(true);
        }, DELAY_TO_OPEN);
      }
    }
  };

  /**
   * This function toggle hovered state of drawer and bind/unbind keydown
   * @param {Boolean} isHovered
   * @param {*} onClick
   */
  setHoveredState = (isHovered, onClick = () => {}) => {
    this.setState(
      {
        hovered: isHovered,
        isOpen: isHovered,
      },
      () => {
        onClick();
      }
    );
    if (isHovered && getViewportInfo().isDesktop) {
      document.body.addEventListener('keydown', this.handleDocumentKeydown);
    } else {
      document.body.removeEventListener('keydown', this.handleDocumentKeydown);
    }
  };

  /**
   * This function will be used to close the l2 links wrapper when user click on any link inside it.
   */

  hideL2Nav = () => {
    this.setHoveredState(false);
  };

  /**
   * This function will be used to close the l2 link items
   */
  onMouseLeave = () => {
    if (getViewportInfo().isDesktop) {
      clearTimeout(this.timeOutHandler);
      this.timeOutHandler = setTimeout(() => {
        this.setHoveredState(false);
      }, DELAY_TO_CLOSE); // Add a delay before closing
    }
  };

  /**
   * This function will be used to open the l2 link items on Enter/Space Press
   */
  onKeyPressed = (e) => {
    if (
      (getViewportInfo().isDesktop && e.keyCode === KEY_CODES.KEY_ENTER) ||
      e.keyCode === KEY_CODES.KEY_SPACE
    ) {
      e.preventDefault();
      this.setHoveredState(true);
    }
  };

  handleFocusBlur = () => {
    const timer = setTimeout(() => {
      document.activeElement.blur();
      clearTimeout(timer);
    }, 0);
    Router.events.off('routeChangeComplete', this.handleFocusBlur);
  };

  getFormatterSizeRange(range) {
    const { shouldRenderMobileNavRedesignedView } = this.props;
    const regex = /SIZES/i;
    if (isEmpty(range)) return '';
    const replacedRange = range.replace(regex, 'Sizes');
    return shouldRenderMobileNavRedesignedView ? `(${replacedRange})` : replacedRange;
  }

  /**
   * This function handles if navigation drawer needs to open on current viewport or now
   * @param {*} onClick
   */
  openNavigationDrawer = (hasL2) => (e) => {
    const {
      onClick,
      trackNavigation,
      clickData,
      customClickEvent,
      shouldRenderMobileNavRedesignedView,
      setHideDrawerInfoSection,
    } = this.props;
    const trackingData = {
      clickData: {
        pageNavigationText: clickData,
        pageType: 'browse',
        clickEvent: true,
        customEvents: [customClickEvent],
      },
    };
    callNavigateAPIForExternalLinks(e);
    if (shouldRenderMobileNavRedesignedView && hasL2) {
      setHideDrawerInfoSection(true);
    }
    if (!getViewportInfo().isDesktop && hasL2) {
      e.preventDefault();
      e.stopPropagation();
      if (trackingData && trackingData.clickData) {
        trackingData.clickData.hasL2 = true;
      }
      this.setHoveredState(true, onClick);
    } else if (getViewportInfo().isDesktop) {
      e.stopPropagation();
      clearTimeout(this.timeOutHandler);
      this.setHoveredState(false);
      Router.events.on('routeChangeComplete', this.handleFocusBlur);
    }
    trackNavigation(trackingData);
    pushEventToAdobeAnalytics('header_click', clickData);
  };

  fetchPromoBadge() {
    const {
      categoryContent: { mainCategory },
    } = this.props;
    return mainCategory && mainCategory.promoBadge;
  }

  fetchValFromMainCategory() {
    const {
      categoryContent: { mainCategory },
    } = this.props;

    const { sizesRange, promoBadge, sizesRangeMobile, promoBadgeMobile } = mainCategory || {};
    const sizeRangeValueDesktop = !isEmpty(sizesRange) ? sizesRange[0].text : '';
    const sizeRangeValueMobile = !isEmpty(sizesRangeMobile) ? sizesRangeMobile[0].text : '';
    const promoBadgeValueDesktop = !isEmpty(promoBadge) ? promoBadge : '';
    const promoBadgeValueMobile = !isEmpty(promoBadgeMobile) ? promoBadgeMobile : '';

    return {
      sizeRangeValueDesktop,
      sizeRangeValueMobile,
      promoBadgeValueDesktop,
      promoBadgeValueMobile,
    };
  }

  /**
   * To render the l1 content\
   */
  renderL1Content = (classForRedContent, name, sizesRange, index, promoBadge, brandThemeColor) => {
    const { shouldRenderMobileNavRedesignedView, hasL2 } = this.props;
    return (
      <div className="nav-bar-l1-content">
        <span
          className={`${
            shouldRenderMobileNavRedesignedView
              ? 'nav-bar-item-label-mobile-redesign'
              : 'nav-bar-item-label'
          } l1-menu ${classForRedContent} ${getBrand() === brand.SNJ && 'snjFont'}${
            this.getFormatterSizeRange(sizesRange) || promoBadge ? '' : ' auto-width'
          }`}
        >
          {name}
        </span>
        <span className="category-size-range">
          {this.getFormatterSizeRange(sizesRange) ||
            (promoBadge && (
              <PromoBadge
                data={promoBadge}
                brandThemeColor={brandThemeColor}
                shouldRenderMobileNavRedesignedView={shouldRenderMobileNavRedesignedView}
              />
            )) ||
            ``}
        </span>
        <span className={`icon-arrow${hasL2 > 0 ? '' : ' hide-arrow'}`} />
      </div>
    );
  };

  renderCategoryRange = (
    sizeRangeValueDesktop,
    sizeRangeValueMobile,
    promoBadgeValueDesktop,
    promoBadgeValueMobile,
    brandThemeColor
  ) => {
    const { isDesktop, isTablet, isMobile } = getViewportInfo() || {};
    const { shouldRenderMobileNavRedesignedView } = this.props;
    if (isMobile && (sizeRangeValueMobile || promoBadgeValueMobile)) {
      return (
        (!isEmpty(sizeRangeValueMobile) && this.getFormatterSizeRange(sizeRangeValueMobile)) ||
        (!isEmpty(promoBadgeValueMobile) && (
          <PromoBadge
            data={promoBadgeValueMobile}
            brandThemeColor={brandThemeColor}
            shouldRenderMobileNavRedesignedView={shouldRenderMobileNavRedesignedView}
          />
        )) ||
        ''
      );
    }
    if (isDesktop || isTablet) {
      return (
        (!isEmpty(sizeRangeValueDesktop) && this.getFormatterSizeRange(sizeRangeValueDesktop)) ||
        (!isEmpty(promoBadgeValueDesktop) && (
          <PromoBadge
            data={promoBadgeValueDesktop}
            brandThemeColor={brandThemeColor}
            shouldRenderMobileNavRedesignedView={shouldRenderMobileNavRedesignedView}
          />
        )) ||
        ``
      );
    }
    return ``;
  };

  renderL1ContentNew = (
    classForRedContent,
    name,
    index,
    sizeRangeValueDesktop,
    sizeRangeValueMobile,
    promoBadgeValueDesktop,
    promoBadgeValueMobile,
    brandThemeColor
  ) => {
    const { shouldRenderMobileNavRedesignedView, hasL2 } = this.props;
    const mobileNonRangeClass =
      (!isEmpty(sizeRangeValueMobile) && this.getFormatterSizeRange(sizeRangeValueMobile)) ||
      !isEmpty(promoBadgeValueMobile)
        ? ''
        : ' mobile-auto-width';
    const tabletAndDesktopNonRangeClass =
      (!isEmpty(sizeRangeValueDesktop) && this.getFormatterSizeRange(sizeRangeValueDesktop)) ||
      !isEmpty(promoBadgeValueDesktop)
        ? ''
        : ' tablet-desktop-auto-width';
    return (
      <div className="nav-bar-l1-content">
        <span
          className={`${
            shouldRenderMobileNavRedesignedView
              ? 'nav-bar-item-label-mobile-redesign'
              : 'nav-bar-item-label'
          } l1-menu ${classForRedContent} ${
            getBrand() === brand.SNJ && 'snjFont'
          }${mobileNonRangeClass}${tabletAndDesktopNonRangeClass}`}
        >
          {name}
        </span>
        <span className="category-size-range">
          {this.renderCategoryRange(
            sizeRangeValueDesktop,
            sizeRangeValueMobile,
            promoBadgeValueDesktop,
            promoBadgeValueMobile,
            brandThemeColor
          )}
        </span>
        <span className={`icon-arrow${hasL2 > 0 ? '' : ' hide-arrow'}`} />
      </div>
    );
  };

  setImgBgColor = () => {
    const { hovered } = this.state;
    return getViewportInfo()?.isDesktop && hovered ? 'new-bg' : '';
  };

  renderPlaceholder = (renderBackground = false) => {
    return renderBackground ? (
      <>
        <div className={`bg`} />
        <div className="image-item-blank" />
      </>
    ) : (
      <div className="image-item" />
    );
  };

  renderImages = (categoryFilterImage = false, renderBackground = false) => {
    return categoryFilterImage?.length > 0 && categoryFilterImage[0]?.url_t !== '' ? (
      <>
        <div className={`bg ${this.setImgBgColor()}`} />
        <Image src={categoryFilterImage[0]?.url_t} className="image-item" />
      </>
    ) : (
      this.renderPlaceholder(renderBackground)
    );
  };

  // eslint-disable-next-line complexity, sonarjs/cognitive-complexity
  render() {
    const {
      categoryContent: { id, name, mainCategory, url, asPath },
      className,
      dataLocator,
      index,
      children,
      removeL1Focus,
      hasL2,
      clickData,
      sizesRange,
      linkOverrideUrl,
      showCondensedHeader,
      isNewNavigationImagesEnabled,
      isNavSizePromoEnabled,
      checkNavHasImages,
      brandThemeColor,
      shouldRenderMobileNavRedesignedView,
      additionalClassName,
      ...others
    } = this.props;
    const { hovered, showNavigationImages, hasMounted } = this.state;
    let classForHovered = '';
    if (hasL2 && hovered && !removeL1Focus) {
      classForHovered = 'is-open';
      this.childRendered = true;
    }
    // If we receive flag showOnlyOnApp then we add this class to links to hide them

    // This class is used to highlight link in red color, it performs check based on id
    const classForRedContent = highlightContent(id);
    // This function renders promoBadge
    const promoBadge = this.fetchPromoBadge(mainCategory);
    // This function renders sizesRange and promoBadge
    const {
      sizeRangeValueDesktop,
      sizeRangeValueMobile,
      promoBadgeValueDesktop,
      promoBadgeValueMobile,
    } = this.fetchValFromMainCategory();

    return (
      <React.Fragment>
        <HideDrawerProvider
          value={{
            hideL2Nav: this.hideL2Nav,
          }}
        >
          <BodyCopy
            component="li"
            className={`${className} ${classForHovered} nav-bar-l1-item${
              additionalClassName || ''
            }`}
            fontFamily="secondary"
            fontSize={['fs13']}
            fontWeight="semibold"
            color="text.primary"
            lineHeight="lh115"
            data-locator={dataLocator}
            onMouseEnter={this.onHover}
            onMouseLeave={this.onMouseLeave}
            id={dataLocator}
            role="menuitem"
            {...others}
          >
            <Anchor
              to={processPath(crossBrandRedirect(linkOverrideUrl || url, name, 'topmenu'), 'to')}
              asPath={processPath(
                crossBrandRedirect(linkOverrideUrl || asPath, name, 'topmenu'),
                'path'
              )}
              onKeyDown={this.onKeyPressed}
              aria-expanded={!!classForHovered}
              handleLinkClick={this.openNavigationDrawer(hasL2)}
              useRouterNavigation={true}
              updateRoute={getViewportInfo()?.isDesktop || !hasL2}
            >
              {isNewNavigationImagesEnabled && !checkNavHasImages && !showCondensedHeader ? (
                <div className="images-row-wrapper">{this.renderImages(false, true)}</div>
              ) : null}
              {showNavigationImages && checkNavHasImages && getViewportInfo()?.isDesktop ? (
                <div className="images-row-wrapper">
                  {this.renderImages(mainCategory?.categoryFilterImage)}
                </div>
              ) : null}

              {isNewNavigationImagesEnabled &&
              checkNavHasImages &&
              !getViewportInfo()?.isDesktop ? (
                <div className="images-row-wrapper">
                  {this.renderImages(mainCategory?.categoryFilterImage)}
                </div>
              ) : null}
              {isNavSizePromoEnabled
                ? this.renderL1ContentNew(
                    classForRedContent,
                    name,
                    index,
                    sizeRangeValueDesktop,
                    sizeRangeValueMobile,
                    promoBadgeValueDesktop,
                    promoBadgeValueMobile,
                    brandThemeColor
                  )
                : this.renderL1Content(
                    classForRedContent,
                    name,
                    sizesRange,
                    index,
                    promoBadge,
                    brandThemeColor
                  )}
            </Anchor>
            {hasMounted && getViewportInfo()?.isDesktop ? (
              <div
                className={hovered || (this.childRendered && hasL2) ? 'show-child' : 'hide-child'}
              >
                {children}
              </div>
            ) : (
              (hovered || (this.childRendered && hasL2)) && children
            )}
            <div
              className={
                hovered && hasL2 ? `${className} l1-overlay ${classForHovered}` : 'hide-child'
              }
              onMouseEnter={this.onMouseLeave}
            />
          </BodyCopy>
        </HideDrawerProvider>
      </React.Fragment>
    );
  }
}

L1NavItem.propTypes = {
  categoryContent: PropTypes.shape({
    mainCategory: PropTypes.shape({
      promoBadge: PropTypes.shape({}),
      categoryFilterImage: PropTypes.shape([
        {
          url_t: '',
        },
      ]),
    }),
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
    asPath: PropTypes.string,
  }).isRequired,
  className: PropTypes.string.isRequired,
  dataLocator: PropTypes.string,
  index: PropTypes.number.isRequired,
  children: PropTypes.element.isRequired,
  onClick: PropTypes.func.isRequired,
  // showOnlyOnApp: PropTypes.bool.isRequired,
  removeL1Focus: PropTypes.bool,
  url: PropTypes.string.isRequired,
  hasL2: PropTypes.number.isRequired,
  clickData: PropTypes.string,
  trackNavigation: PropTypes.func.isRequired,
  sizesRange: PropTypes.string,
  linkOverrideUrl: PropTypes.string,
  removeL1PanelTimeOutItem: PropTypes.func,
  isOpen: PropTypes.bool,
  showCondensedHeader: PropTypes.bool,
  isNewNavigationImagesEnabled: PropTypes.bool,
  checkNavHasImages: PropTypes.bool,
  isNavSizePromoEnabled: PropTypes.bool,
};

L1NavItem.defaultProps = {
  dataLocator: '',
  clickData: '',
  sizesRange: '',
  linkOverrideUrl: '',
  removeL1Focus: undefined,
  removeL1PanelTimeOutItem: () => {},
  isOpen: false,
  showCondensedHeader: false,
  isNewNavigationImagesEnabled: false,
  checkNavHasImages: false,
  isNavSizePromoEnabled: false,
};

const mapStateToProps = (state) => {
  return {
    isOpen: state.Header.isL1PanelOpen,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    removeL1PanelTimeOutItem: () => dispatch(removeL1PanelTimeOut()),
  };
};

export { L1NavItem as L1NavItemVanilla };
export default connect(mapStateToProps, mapDispatchToProps)(withStyles(L1NavItem, style));
